import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/atoms/card';
import { Input } from '@/components/atoms/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/molecules/form-field';
import { WEIGHT_UNITS, WEIGHT_UNITS_BASIC } from '@/lib/constants/form-options';
import type { CreateProductFormData, UpdateProductFormData } from '../types/product-form.types';

interface ProductWeightProps<T extends CreateProductFormData | UpdateProductFormData> {
  form: UseFormReturn<T>;
  useBasicUnits?: boolean;
}

export function ProductWeight<T extends CreateProductFormData | UpdateProductFormData>({
  form,
  useBasicUnits = false,
}: ProductWeightProps<T>) {
  const { t } = useTranslation();
  const weightUnits = useBasicUnits ? WEIGHT_UNITS_BASIC : WEIGHT_UNITS;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('products.weight')} *</CardTitle>
        <CardDescription>
          {t('products.weightDescription', 'Le poids aide à calculer les frais de livraison')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name={'weight' as any}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('products.weightValue')} *</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder="0.00"
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) => {
                      const val = e.target.valueAsNumber;
                      field.onChange(isNaN(val) ? 0 : val);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'weightUnit' as any}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('products.weightUnit')} *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value || 'grams'}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t('products.selectWeightUnit')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {weightUnits.map((unit) => (
                      <SelectItem key={unit.value} value={unit.value}>
                        {unit.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </CardContent>
    </Card>
  );
}
